import { Outlet, createFileRoute, redirect } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";

import { AcceleratorCheckoutReturn } from "~/components/accelerator/accelerator-checkout-return";
import { NovaCheckoutReturn } from "~/components/nova/nova-checkout-return";
import { CoachPrivateProfileProvider } from "~/contexts/coach";
import { authMiddleware } from "~/lib/auth-middleware";
import { deriveAccountStatus } from "~/lib/functions/coach-status";
import { coachAccountQueryOptions } from "~/lib/queries/coachAccountQuery";
import { coachStatusQueryOptions } from "~/lib/queries/coachStatusQuery";
import type { CoachPrivateData } from "~/shared/loaders";

/** Load coach with full relations using Clerk auth (clerkUserId → email fallback). */
const loadCoachForClerkUser = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .handler(async ({ context }): Promise<CoachPrivateData | null> => {
    const { clerkUserId, email, id: dbUserId } = context.user;
    if (!email) return null;

    const { findAndLinkCoachForClerkUser } =
      await import("~/lib/server/coach-clerk-link");
    const coach = await findAndLinkCoachForClerkUser(
      clerkUserId,
      email,
      dbUserId,
    );
    if (!coach) return null;

    const { getDBWithInit } = await import("~/lib/db");
    const coachWithRelations = await (
      await getDBWithInit()
    ).query.coaches.findFirst({
      where: { id: coach.id },
      with: {
        profile: true,
        details: true,
        mainOffer: {
          with: { tags: true, benefits: true },
        },
        resources: true,
        certifications: true,
        testimonials: true,
        results: true,
      },
    });

    if (!coachWithRelations) return null;

    const {
      getActiveWorkshopsForCoach,
      getSummitsOpenForSpecialOffers,
      getSummitsWithCoachWorkshops,
    } = await import("~/lib/server/summit");
    const referenceDate = new Date();
    const activeSummitWorkshops = await getActiveWorkshopsForCoach(
      coach.id,
      referenceDate,
    );
    const coachWorkshopSummits = await getSummitsWithCoachWorkshops(coach.id);
    const summitOfferSummits = coachWithRelations.isFeaturedCoach
      ? await getSummitsOpenForSpecialOffers(referenceDate)
      : coachWorkshopSummits;

    return {
      ...coachWithRelations,
      activeSummitWorkshops,
      summitOfferSummits,
    };
  });

export const Route = createFileRoute("/_user/my-coaching")({
  // No title: the coach menu names each page's section in the trail, and a
  // page outside the menu keeps its own title alone.
  beforeLoad: async ({ context }) => {
    // The profile is cached for 5 min so its `approvedAt` can be stale; read
    // tier-1 approval fresh alongside it (a cheap query that always revalidates)
    // so a just-approved coach isn't held on the "Application Under Review"
    // screen for the whole session (issue #900). Run both in parallel to avoid
    // adding a round-trip on entry.
    const [coach, status] = await Promise.all([
      context.queryClient.ensureQueryData({
        queryKey: ["my-coaching-profile"],
        queryFn: () => loadCoachForClerkUser(),
        staleTime: 5 * 60 * 1000,
      }),
      // Never let a transient status-read failure fail the whole route — fall
      // through to the profile-derived status below (Promise.all would reject
      // otherwise, bypassing that fallback).
      context.queryClient
        .fetchQuery(coachStatusQueryOptions(context.user.clerkUserId))
        .catch(() => null),
    ]);

    if (!coach) {
      throw redirect({ to: "/dashboard" });
    }

    // The billing lifecycle, loaded once here so child routes read one value
    // instead of each deriving their own. Sequenced after the coach load rather
    // than run beside it: on a coach's first Clerk visit that load is what
    // writes `clerkUserId` onto the coach row and claims a payment-link
    // subscription, and this read resolves the coach by `clerkUserId` alone —
    // run in parallel it would miss the row entirely or read it mid-claim and
    // report a stale `pre_trial`. A transient failure still must not take the
    // coach area down, so it arrives as null and a consumer decides without it.
    const account = await context.queryClient
      .fetchQuery(coachAccountQueryOptions(context.user.clerkUserId))
      .catch(() => null);

    // Fall back to the profile-derived status if the status read is unavailable.
    const accountStatus = status?.accountStatus ?? deriveAccountStatus(coach);
    const isRevoked = status?.isRevoked ?? !!coach.tier1RevokedAt;
    return { coach, accountStatus, isRevoked, account };
  },
  loader: ({ context }) => ({
    coach: context.coach,
    accountStatus: context.accountStatus,
    isRevoked: context.isRevoked,
    account: context.account,
  }),
  component: MyCoachingLayout,
});

function MyCoachingLayout() {
  const { coach } = Route.useLoaderData();
  const userId = Route.useRouteContext({
    select: (context) => context.user.clerkUserId,
  });

  return (
    <CoachPrivateProfileProvider coachProfile={coach}>
      <NovaCheckoutReturn userId={userId}>
        <AcceleratorCheckoutReturn />
        <Outlet />
      </NovaCheckoutReturn>
    </CoachPrivateProfileProvider>
  );
}
